import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { initializeThemes } from './utils/themeManager';
import { DtScrollbarDirective, DtTooltipDirective } from '@dialpad/dialtone/vue3';
import 'overlayscrollbars/overlayscrollbars.css';
import { simpleDebugLog } from './utils/simpleDebugLogger';

//  Import Global Styles
import '@dialpad/dialtone/css';
import '@dialpad/dialtone/css-default-theme';
import './styles/main.less';
// Import theme files
import './styles/themes.css';
import './styles/density.css';
import './styles/appearance-fixes.css';

// Add global unhandled promise rejection handler
function isMessageChannelOrPortError(reason: unknown): boolean {
  if (reason && typeof reason === 'object' && 'message' in reason) {
    const message = String((reason as { message: unknown }).message).toLowerCase();
    return (
      message.includes('message channel closed') ||
      message.includes('message port closed') ||
      message.includes('listener indicated an asynchronous response')
    );
  }
  return false;
}

window.addEventListener('unhandledrejection', event => {
  simpleDebugLog.warn('Unhandled promise rejection:', event.reason);

  // Handle specific known promise rejections that are expected
  if (isMessageChannelOrPortError(event.reason)) {
    // Log the error for debugging but don't suppress it
    simpleDebugLog.error('Message channel/port error detected:', event.reason);
    return; // Don't call preventDefault() - let the error surface
  }
});

const app = createApp(App);
const pinia = createPinia();

// One-time theme migration: force all users to 'system' theme if not migrated yet
if (!localStorage.getItem('theme_migrated_to_system')) {
  localStorage.setItem('theme', 'system');
  localStorage.setItem('theme_migrated_to_system', 'true');
}

pinia.use(piniaPluginPersistedstate);

// Set up DevTools in development and preview environments
const isDevelopment = import.meta.env.MODE === 'development';
const isPreview = import.meta.env.PROD && window.location.hostname.includes('github'); // GitHub Pages
const isStaging =
  import.meta.env.PROD &&
  (window.location.hostname.includes('preview') || window.location.hostname.includes('staging'));
const devToolsEnabled = import.meta.env.VITE_ENABLE_DEVTOOLS === 'true';

if (isDevelopment || isPreview || isStaging || devToolsEnabled) {
  import('./plugins/devTools')
    .then(module => {
      const devToolsPlugin = module.default;
      app.use(devToolsPlugin);
    })
    .catch(error => {
      simpleDebugLog.warn('Failed to load DevTools:', error);
    });
}

app.use(DtScrollbarDirective).use(DtTooltipDirective).use(router).use(pinia);

// Initialize themes after the app is mounted and DOM is ready
app.mount('#app');

// Initialize themes after mounting to ensure DOM is available (now async)
initializeThemes().catch(error => {
  simpleDebugLog.warn('Failed to initialize themes:', error);
});
